'use client';

import { UiButtonLabel } from '@/components/ui/UiButtonLabel';
import { UiIcon } from '@/components/ui/UiIcon';
import { UiSection } from '@/components/ui/UiSection';
import { useConsent, useServiceConsentGranted } from '@/features/consent';

/** Matches the catalog entry in consent-catalog.constants.ts. */
const MAP_SERVICE_ID = 'openstreetmap';

type LocationMapProps = {
    consentTitle: string;
    consentText: string;
    consentButtonLabel: string;
    embedUrl: string;
    routeLabel?: string;
    routeUrl?: string;
    mapTitle: string;
    /** Shown under the consent button, so the location is readable without loading the map. */
    address?: string;
    /** Inside another block's layout: the card without its own section frame. */
    embedded?: boolean;
};

/**
 * The map frame is only inserted once the map service is granted — by the button
 * here or in the cookie settings: until then no request reaches the map provider,
 * so no visitor data leaves the site. The card grows with its column; the consent
 * note stays centred in it.
 */
export function LocationMap({
    consentTitle,
    consentText,
    consentButtonLabel,
    embedUrl,
    routeLabel,
    routeUrl,
    mapTitle,
    address,
    embedded = false,
}: LocationMapProps) {
    const { grantService } = useConsent();
    const isGranted = useServiceConsentGranted(MAP_SERVICE_ID);

    return (
        <UiSection embedded={embedded} className="section-pt-sm">
            <div className="relative flex min-h-70 flex-col overflow-hidden rounded-(--r) border border-(--border) bg-(--bg-alt)">
                {isGranted ? (
                    <>
                        <iframe
                            src={embedUrl}
                            title={mapTitle}
                            loading="lazy"
                            referrerPolicy="no-referrer-when-downgrade"
                            className="absolute inset-0 size-full border-0"
                        />
                        {routeUrl && routeLabel ? (
                            <a
                                href={routeUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="btn btn-primary absolute right-4 bottom-4"
                            >
                                <UiButtonLabel>{routeLabel}</UiButtonLabel>
                            </a>
                        ) : null}
                    </>
                ) : (
                    <div className="flex flex-1 flex-col items-center justify-center p-8 text-center">
                        <span className="flex size-13 items-center justify-center rounded-(--r-md) border border-(--border) bg-(--surface) text-(--accent)">
                            <UiIcon name="map-trifold" size={26} aria-hidden="true" />
                        </span>

                        <p className="card-title mt-4">{consentTitle}</p>
                        <p className="mt-2 max-w-[34ch] text-sm text-(--muted)">{consentText}</p>

                        {/* Same switch as OpenStreetMap in the cookie settings; other services stay untouched. */}
                        <button
                            type="button"
                            onClick={() => grantService(MAP_SERVICE_ID)}
                            className="btn btn-primary mt-4.5"
                        >
                            <UiButtonLabel>{consentButtonLabel}</UiButtonLabel>
                            <UiIcon name="arrow-right" size="1.25em" className="shrink-0" aria-hidden="true" />
                        </button>

                        {address ? (
                            <address className="mt-3 text-xs not-italic text-(--muted)">{address}</address>
                        ) : null}
                    </div>
                )}
            </div>
        </UiSection>
    );
}
